import { Course } from "../providers/coursesProvider";
import { useRef, useState } from "react";
import { Row } from "../misc/Row";
import { StudentSelect } from "../inputs/StudentSelect";
import { isASFCourseSession, isTheoryLesson } from "../model/autovioCalendarEvents";
import { Box, Button, CircularProgress, IconButton, SxProps } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import { ConfirmCancellationPolicyDialog } from "../misc/calendar/ConfirmCancellationPolicyDialog";
import { useDialog } from "../hooks/useDialog";
import { Student } from "../providers/studentsProvider";
import { useGetOne } from "react-admin";
import { useAutovioContext } from "../hooks/useAutovioContext";
import { CancellationPolicy } from "../model/CancellationPolicy";
import { serverAPI } from "../api/server.api";

export function AddStudentToCourseButton(props: { sx?: SxProps; course: Course; onClick?: () => void }) {
  const { sx, course, onClick } = props;
  const [{ drivingSchoolId }] = useAutovioContext();
  const { data: drivingSchool } = useGetOne("drivingSchools", { id: drivingSchoolId });
  const [showStudentSelect, setShowStudentSelect] = useState(false);
  const studentRef = useRef<Student | null>(null);
  const {
    isDialogOpen: isConfirmDialogOpen,
    openDialog: openConfirmDialog,
    closeDialog: closeConfirmDialog,
  } = useDialog();
  const [addingStudent, setAddingStudent] = useState(false);
  const isTheoryCourse = course.events.length > 0 && isTheoryLesson(course.events[0]);
  const isASFCourse = course.events.length > 0 && isASFCourseSession(course.events[0]);

  if (!drivingSchool) {
    return null;
  }

  let filterStudents: (student: Student) => boolean;
  if (isTheoryCourse) {
    filterStudents = (student) => !student.archived;
  } else if (isASFCourse) {
    filterStudents = (student) => student.bookedTrainings.some((it) => it.isASF) && !student.archived;
  } else {
    filterStudents = (student) => student.bookedQuotes.some((it) => it.bundleName === course.name) && !student.archived;
  }

  const handleStudentSelected = (student: Student | null) => {
    studentRef.current = student;
    if (!student) {
      return;
    }
    if (isTheoryCourse && drivingSchool.customizations.includes("cancellationPolicyPerTheoryAppointment")) {
      openConfirmDialog();
    } else {
      void addStudentToCourse();
    }
  };

  const addStudentToCourse = async (agreedCancellationPolicy?: CancellationPolicy) => {
    const student = studentRef.current;
    if (!student) {
      throw new Error("Invalid state: No student selected");
    }
    setAddingStudent(true);
    await runSafely(() => serverAPI.addStudentToCourse({ student, course, agreedCancellationPolicy }), {
      errorMessage: `Fehler beim Hinzufügen von ${student.callName}.`,
      successMessage: `${student.callName} erfolgreich hinzugefügt.`,
    });
    setAddingStudent(false);
  };

  return (
    <>
      {showStudentSelect ? (
        <Row sx={sx} alignItems="center" spacing="10px">
          <StudentSelect
            autoFocus
            label="Teilnehmer hinzufügen"
            disabled={addingStudent}
            sx={{ minWidth: "250px", flex: 1 }}
            size="small"
            onChange={(student) => handleStudentSelected(student)}
            drivingSchoolId={drivingSchoolId}
            filterStudents={filterStudents}
          />
          {addingStudent ? (
            <CircularProgress size={24} sx={{ color: "text.secondary" }} />
          ) : (
            <IconButton size="small" onClick={() => setShowStudentSelect(false)}>
              <CloseIcon sx={{ color: "text.primary" }} />
            </IconButton>
          )}
        </Row>
      ) : (
        <Box sx={sx}>
          <Button
            variant="outlined"
            sx={{ height: "40px" /* ... same height as <StudentSelect> */ }}
            startIcon={<AddIcon />}
            onClick={onClick ?? (() => setShowStudentSelect(true))}
          >
            Teilnehmer
          </Button>
        </Box>
      )}
      <ConfirmCancellationPolicyDialog
        isOpen={isConfirmDialogOpen}
        studentUid={studentRef.current?.id}
        onCancel={() => {
          closeConfirmDialog();
          setShowStudentSelect(false);
          studentRef.current = null;
        }}
        onConfirm={async () => {
          closeConfirmDialog();
          await addStudentToCourse(drivingSchool.theoryLessonCancellationPolicy);
          setShowStudentSelect(false);
          studentRef.current = null;
        }}
      />
    </>
  );
}
